import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGetSpeciality } from '../../hooks/useGetSpeciality';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { useSpecialtiesResult } from '../../hooks/useSpecialties';
import { BhProviderIcons } from '../../models/Lagoon';
import { ProviderType } from '../../models/ProviderDetails';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePlanSelectorStore } from '../../store/usePlanSelectorStore';
import { getPlanIndicator, scrollIntoViewById } from '../../utils/common';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getBhIcon } from '../../utils/iconography.utils';
import { parseProviderName } from '../../utils/providerNames.utils';
import { getShowCost } from '../../utils/showCost.utils';
import {
  getLoggedInMember,
  getMarketType,
  getProductCodeForCoverageType,
  useCustomPolicy,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { BookmarkButton } from '../BookmarkButton/BookmarkButton';
import { ReverseCoverageTypesCodes } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';
import { CostIndicatorPopover } from '../DataCard/CostIndicatorPopover';
import { VoiceOnlyText } from '../DataCard/DataCard.style';
import { ImageComponent } from '../ImageComponent';
import { getFeatureFlag } from '../Utils';
import {
  defaultStringToEmpty,
  getAcoBenefitPlan,
} from '../Widget/SmartChoiceWidget/utils';
import {
  AvatarDetailsPage,
  CostIndicatorIconStyle,
  DetailsSectionWrap,
  DetailsSubSection,
  MarginStyle,
  ProviderIndicatorSection,
  ProviderSubtitle,
  ProviderSubtitleSection,
  ProviderTitle,
  displayBookMarkIconStyle,
  displayVirtualVisitStyle,
} from './DetailsPageHeader.styled';
import { InNetworkStatusLabel } from './InNetworkStatusLabel';
import { ProviderDetailsRating } from './ProviderDetailsRating';
import { VirtualCareOfferedLabel } from './VirtualCareOfferedLabel';

export type Props = {
  costEnableFlag: boolean;
  suppressCostEstimates: boolean;
  suppressRatings: boolean;
  imageLocation?: string;
  providerType?: ProviderType;
  displayVirtualCareIcon: boolean;
  providerName: string;
  professionalDesignation?: string;
  nameAttrs: string;
  primarySpeciality: string[];
  inNetwork: string;
  rating?: string;
  virtualCareOffered: boolean;
  costIndicator: string;
  surveyCount?: number;
  coverageType?: string;
  handleTabChange: (a: number) => void;
  providerId: string;
  providerLocations?: any[];
  providerDetails?: any;
  networkId?: string;
};

export const DetailsSection = ({
  costEnableFlag,
  costIndicator,
  suppressCostEstimates,
  suppressRatings,
  imageLocation,
  providerType,
  displayVirtualCareIcon,
  providerName,
  professionalDesignation,
  nameAttrs,
  primarySpeciality,
  inNetwork,
  rating,
  virtualCareOffered,
  surveyCount,
  coverageType,
  handleTabChange,
  providerId,
  providerDetails,
  providerLocations = [],
  networkId,
}: Props) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const imgSrc = imageLocation;
  const mobileScreen = useMediaQuery(phoneOnly);
  const [providerRatings] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.GET_PROVIDER_RATINGS,
  ]);
  const { portalData } = useContext(PortalContext);
  const portalShowCost = getShowCost(portalData);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const bhProviderIcons: BhProviderIcons[] = useLagoon('bh_provider_icons')();
  const featureFlags = useLagoon('feature-flags')();
  const { data: bhSpecialtyCodes = [] } = useSpecialtiesResult({
    coverageType: 'B',
  });
  const speciality = useGetSpeciality({ primarySpeciality });
  const isTieredPlan =
    searchParams.get('isTieredPlan') === 'true' ? true : false;
  const acoProvider = searchParams.get('acoProvider') === 'true' ? true : false;

  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  const enableCareTeamView = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CARE_TEAM_VIEW
  );

  const enableRemoveEstimatedCostText = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_REMOVE_ESTIMATED_COST_TEXT
  );

  const showNetworkInfo = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_PROVIDER_NETWORK_INFO
  );

  const planInfo = usePlanSelectorStore((state) => state.planInfo);

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { lob } = chipStore;

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();

  const suppressVirtualCare =
    isVirtualCareQuickCareSuppressed ||
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isVirtualCareUnetRailroadSuppressed);

  const bhIcon =
    coverageType === 'B'
      ? getBhIcon([], primarySpeciality, bhProviderIcons, bhSpecialtyCodes)
      : '';
  const renderAboutTab = () => {
    if (rating !== null) {
      handleTabChange(0);
      setTimeout(() => {
        const isGuestPlanSelection =
          portalData?.portalConfig?.basePath?.startsWith(
            '/guest-plan-selection'
          );
        const offset = isGuestPlanSelection ? 260 : 48;
        scrollIntoViewById('patient-reviews-summary', undefined, offset);
      }, 100);
    }
  };

  const VoiceA11yScreenReader = (
    <VoiceOnlyText>
      {rating
        ? `${rating} ${t('stars')} ${t('out of 5')} ${surveyCount} ${t(
            'reviews'
          )}`
        : t('PROVIDER_DETAILS.ABOUT_TAB.REVIEW_SECTION.RATING_NOT_AVAILABLE')}
    </VoiceOnlyText>
  );

  const selectedMember = getLoggedInMember();
  const { isCustomPreferredPolicy } = useCustomPolicy(
    selectedMember,
    useLagoon('CUSTOM_PREFERRED_POLICY'),
    lob
  );

  const allConfigs = useLagoon('config')();
  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;

  const isAcoBenefitPlan = getAcoBenefitPlan(
    defaultStringToEmpty(
      getProductCodeForCoverageType(
        selectedMember,
        ReverseCoverageTypesCodes.MEDICAL
      )
    ),
    defaultStringToEmpty(getMarketType(selectedMember)),
    acoBenefitPlanCollection
  );
  const smartChoiceEligibility = useSmartChoice();
  const smartChoicePriceCheck =
    !smartChoiceEligibility.isEnabled ||
    (isTieredPlan === false &&
      !isAcoBenefitPlan &&
      acoProvider === false &&
      !isCustomPreferredPolicy);

  const planList = showNetworkInfo
    ? getPlanIndicator(planInfo, networkId, coverageType)
    : [];
  return (
    <DetailsSectionWrap>
      <Flex css={displayBookMarkIconStyle}>
        {enableCareTeamView && !isEligibleForStandaloneDental && (
          <BookmarkButton
            providerDetails={providerDetails}
            providerId={providerId}
            providerLocationsDetailPage={providerLocations}
            providerName={parseProviderName(
              providerName,
              providerType,
              professionalDesignation,
              nameAttrs
            )}
            providerType={providerType}
          />
        )}
      </Flex>
      <Flex
        css={{
          'abyss-flex-root': {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '-20px',
          },
        }}
      >
        <AvatarDetailsPage
          data-auto-testid="provider-profile-picture"
          data-testid="provider-profile-picture"
        >
          {ImageComponent({
            imgRef,
            providerType,
            imgSrc,
            bhIcon,
            sizeToken: '$detailsIconSize',
          })}
        </AvatarDetailsPage>
        {displayVirtualCareIcon && !suppressVirtualCare && (
          <Flex
            css={displayVirtualVisitStyle}
            data-auto-testid="videocam-icon"
            data-testid="videocam-icon"
          >
            <IconSymbol
              color="$core.color.brand.100"
              icon="videocam"
              size={16}
              variant="filled"
            />
          </Flex>
        )}
      </Flex>
      <DetailsSubSection>
        <ProviderTitle
          color="$core.color.brand.100"
          data-auto-testid="provider-title"
          data-testid="provider-title"
          size="xs"
        >
          {parseProviderName(
            providerName,
            providerType,
            professionalDesignation,
            nameAttrs
          )}
        </ProviderTitle>
        <ProviderSubtitleSection
          data-auto-testid="provider-subtitle"
          data-testid="provider-subtitle"
        >
          <Flex>
            {primarySpeciality?.length > 0 && (
              <ProviderSubtitle
                color="$core.color.brand.100"
                fontSize={mobileScreen ? 'md' : 'lg'}
              >
                {speciality}
              </ProviderSubtitle>
            )}
            {/* Renders InNetworkStatus and Cost Indicator, Virtual Care labels in the same row for viewports greater than mobile. */}
            {!mobileScreen && (
              <Flex direction="row">
                {planList && planList?.length >= 1 ? (
                  planList?.map((plan: string) => (
                    <InNetworkStatusLabel
                      inNetwork={inNetwork}
                      key={plan}
                      networkInfo={plan}
                    />
                  ))
                ) : (
                  <InNetworkStatusLabel
                    inNetwork={inNetwork}
                    networkInfo={''}
                  />
                )}

                <CostIndicatorIconStyle>
                  {portalShowCost &&
                    costEnableFlag &&
                    costIndicator &&
                    !enableRemoveEstimatedCostText &&
                    !suppressCostEstimates &&
                    providerType === ProviderType.PRACTITIONER &&
                    smartChoicePriceCheck && (
                      <MarginStyle>
                        <CostIndicatorPopover
                          callFrom="detailpageheader"
                          costIndicator={costIndicator}
                          providerType={providerType}
                        />
                      </MarginStyle>
                    )}
                </CostIndicatorIconStyle>
                {!suppressVirtualCare && (
                  <Layout.Group data-testid="Offers-virtual-care-label">
                    <VirtualCareOfferedLabel
                      virtualCareOffered={displayVirtualCareIcon}
                    />
                  </Layout.Group>
                )}
              </Flex>
            )}
          </Flex>
          {/* Renders InNetworkStatus and Cost Indicator in the same row, and renders Virtual Care Offered label on next row for mobile view only. */}
          {mobileScreen && (
            <Flex
              css={{
                marginTop: '12px',
              }}
            >
              {planList && planList?.length >= 1 ? (
                planList.map((plan: string) => (
                  <InNetworkStatusLabel
                    inNetwork={inNetwork}
                    key={plan}
                    networkInfo={plan}
                  />
                ))
              ) : (
                <InNetworkStatusLabel inNetwork={inNetwork} networkInfo={''} />
              )}
              <CostIndicatorIconStyle>
                {portalShowCost &&
                  costEnableFlag &&
                  costIndicator &&
                  !enableRemoveEstimatedCostText &&
                  !suppressCostEstimates &&
                  providerType === ProviderType.PRACTITIONER && (
                    <MarginStyle>
                      <CostIndicatorPopover
                        callFrom="detailpageheader"
                        costIndicator={costIndicator}
                        providerType={providerType}
                        selectedProvider={providerName}
                      />
                    </MarginStyle>
                  )}
              </CostIndicatorIconStyle>
            </Flex>
          )}
          {/* Pushing Virtual care label to next row for mobile view only. */}
          {virtualCareOffered && mobileScreen && !suppressVirtualCare ? (
            <ProviderIndicatorSection>
              <VirtualCareOfferedLabel
                virtualCareOffered={displayVirtualCareIcon}
              />
            </ProviderIndicatorSection>
          ) : null}

          {!suppressRatings && providerType === ProviderType.PRACTITIONER && (
            <Button
              css={{
                'abyss-button-root': {
                  minHeight: '0px',
                  padding: '2px',
                  borderRadius: '2px',
                  height: 'auto',
                  fontWeight: 400,
                  fontSize: mobileScreen ? '$md' : '$lg',
                  ...(mobileScreen && {
                    width: 'fit-content',
                    marginTop: '4px',
                  }),
                  ...(rating === null && {
                    cursor: 'default',
                    backgroundColor: 'transparent !important',
                    color: 'transparent !important',
                  }),
                },
              }}
              data-testid="provider-detail-clickable-reviews"
              isDisabled={rating === null}
              onClick={renderAboutTab}
              size="$sm"
              variant="text"
            >
              <Flex alignItems="center">
                {VoiceA11yScreenReader}
                <ProviderDetailsRating
                  providerType={providerType}
                  rating={rating}
                  {...(providerRatings && {
                    hideNumber: true,
                    surveyCount,
                  })}
                />
              </Flex>
            </Button>
          )}
        </ProviderSubtitleSection>
      </DetailsSubSection>
    </DetailsSectionWrap>
  );
};
